import React, { useEffect, useRef } from 'react';
import G6, { Graph, NodeConfig, TreeGraph } from '@antv/g6';
import ResizeObserver from 'rc-resize-observer';
import { highlightRecursive, nodeGap, nodeSize, registerG6 } from './registerG6';
import { GraphProps } from '../../utils';
import { Modal, Themes } from '@maxtropy/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';

interface Props {
  theme: Themes;
  data?: GraphProps;
  fullScreen?: boolean;
  focusId?: number;
  busCouplers?: GraphProps[];
  setCurrentCircuitInfo: (v: any) => void;
  setCircuitDetailVisible: (v: boolean) => void;
}

const StationGraph: React.FC<Props> = ({
  data,
  theme,
  focusId,
  fullScreen,
  setCurrentCircuitInfo,
  busCouplers,
  setCircuitDetailVisible,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<TreeGraph>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const url_circuitId = urlSearchParams.get('circuitId') || undefined;

  useEffect(() => {
    registerG6(
      false,
      id => {
        navigate(`/dmes/circuit/topologicalGraph?circuitId=${id}`, { replace: true });
      },
      theme
    );
  }, [navigate, theme]);

  // 站房模式
  useEffect(() => {
    if (graphRef.current) {
      graphRef.current?.clear();
    }
    if (!data?.value) {
      return;
    }
    if (!containerRef.current) return;

    const graph = new G6.TreeGraph({
      container: containerRef.current,
      linkCenter: true,
      width: containerRef.current.scrollWidth,
      height: containerRef.current.scrollHeight,
      modes: {
        default: ['drag-canvas', 'zoom-canvas'],
      },
      fitCenter: false,
      defaultNode: {
        type: 'customNodeWithData',
      },
      defaultEdge: {
        type: 'customLine',
        style: {
          endArrow: true,
        },
      },
      layout: {
        type: 'indented',
        direction: 'LR',
        dropCap: false,
        indent: nodeSize[0] + nodeGap[0],
        getHeight: () => {
          return nodeGap[1];
        },
      },
    });
    graphRef.current = graph;

    graph.on('afterlayout', () => {
      graph.moveTo(0, 60);

      if (!busCouplers?.length) return;

      busCouplers.forEach(coupler => {
        const targetId = coupler.value.target?.circuitId;
        const couplerId = coupler.value.circuitId;

        if (!targetId) return;

        const sourceNode = graph.findById(String(targetId));
        const targetNode = graph.findById(String(couplerId));

        if (sourceNode && targetNode) {
          // 避免重复添加
          const exists = graph.findAll('edge', edge => {
            const model = edge.getModel();
            return model.source === String(targetId) && model.target === String(couplerId);
          }).length;

          if (!exists) {
            graph.addItem('edge', {
              source: String(targetId),
              target: String(couplerId),
              type: 'customLine',
              sourceAnchor: 1,
              targetAnchor: 0,
              style: { endArrow: true },
            });
          }
        }
      });
    });

    graph.data(data);
    graph.render();
    graph.layout();

    const rootNode = graph.getNodes().find(node => {
      const model = node.getModel();
      return !model.parentId; // 根节点没有 parentId
    });

    if (rootNode) {
      graph.hideItem(rootNode); // 隐藏根节点
      const outEdges = rootNode.getOutEdges(); // 获取根节点所有出边
      outEdges.forEach(edge => graph.hideItem(edge)); // 隐藏所有出边
    }

    graph.on('node:click', e => {
      const shapeName = e.target.get('name');
      const nodeModel = e.item!.getModel();

      // 展开收缩节点按钮
      if (shapeName.startsWith('collapse')) {
        nodeModel.collapsed = !nodeModel.collapsed;
        // @ts-ignore
        graph.setItemState(e.item!, 'collapsed', nodeModel.collapsed);
        graph.layout();
      }
      // 点击节点事件
      if (shapeName.startsWith('main-box')) {
        graph.getNodes().forEach(node => node.setState('isSelected', false));
        nodeModel.isSelected = true;
        const nodeId = e.item!.getID();
        highlightRecursive(graph, nodeId);

        const data = nodeModel as GraphProps & NodeConfig;
        if (!data.value.hasOu) {
          setCurrentCircuitInfo(undefined);
          Modal.warning({
            title: `当前无【${data.value.name}】的数据权限，请联系管理员添加。`,
          });
        } else {
          setCurrentCircuitInfo(data);
          setCircuitDetailVisible(true);
        }
      }
    });

    // hover事件
    graph.on('node:mouseenter', evt => {
      const item = evt.item;
      const shapeName = evt.target.get('name');
      if (shapeName.startsWith('main-box')) {
        graph.setItemState(item!, 'hover', true);
      }
      if (shapeName.startsWith('collapse')) {
        graph.setItemState(item!, 'collapse-hover', true);
      }
      if (shapeName.startsWith('bus-coupler')) {
        graph.setItemState(item!, 'bus-coupler-hover', true);
      }
    });

    graph.on('node:mouseleave', evt => {
      const item = evt.item;
      graph.setItemState(item!, 'hover', false);
      graph.setItemState(item!, 'collapse-hover', false);
      graph.setItemState(item!, 'bus-coupler-hover', false);
    });

    return () => {
      graph.destroy();
    };
  }, [busCouplers, data, theme]);

  useEffect(() => {
    if (!graphRef.current) return;
    if (url_circuitId) {
      const targetNode = graphRef.current?.findById(url_circuitId);
      if (targetNode) {
        // 居中 + 高亮
        graphRef.current.getNodes().forEach(node => node.setState('isSelected', false)); // 取消其他选中
        targetNode.setState('isSelected', true);
        graphRef.current.focusItem(targetNode, true);
      }
    }
  }, [url_circuitId, graphRef.current]);

  const onResize = debounce((size: { width: number; height: number }) => {
    if (!containerRef.current) return;
    if (!graphRef.current) return;
    graphRef.current?.changeSize(size.width, size.height);
  }, 300);

  return (
    <ResizeObserver onResize={onResize}>
      <div
        style={{
          height: fullScreen ? '100vh' : 'calc(100vh - 216px)',
          width: '100%',
          border: '1px solid var(--mx-base-border-color)',
          backgroundImage: 'var(--mx-bg-image)',
        }}
        ref={containerRef}
      ></div>
    </ResizeObserver>
  );
};

export default StationGraph;
